import { useMemo } from 'react';
import { useQuery } from 'urql';
import PageHeader from '../../../components/PageHeader.jsx';
import StickyActions from '../../../components/StickyActions.jsx';
import { KARDEX_POR_PRODUCTO, STOCK_ACTUAL } from './gql.js';

function formatNumber(value, fractionDigits = 2) {
  return Number(value ?? 0).toLocaleString('es-AR', {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });
}

export default function KardexDetail({ context, onBack }) {
  const { productoId, sucursalId, desde, hasta, movimiento } = context ?? {};

  const [{ data, fetching, error }] = useQuery({
    query: KARDEX_POR_PRODUCTO,
    variables: {
      productoId,
      sucursalId: sucursalId || null,
      desde: desde || null,
      hasta: hasta || null,
      pagination: { limit: 200, offset: 0 },
    },
    pause: !productoId,
  });

  const [{ data: stockData }] = useQuery({
    query: STOCK_ACTUAL,
    variables: {
      productoId,
      sucursalId: sucursalId || null,
    },
    pause: !productoId,
  });

  const movimientos = data?.kardexPorProducto?.items ?? [];
  const resumen = stockData?.stockActual;

  const titulo = useMemo(() => {
    if (!productoId) return 'Detalle Kardex';
    return `Kardex producto #${productoId}`;
  }, [productoId]);

  return (
    <div className="mx-auto flex w-full max-w-[1400px] flex-col gap-6 px-6 py-8">
      <PageHeader
        title={titulo}
        subtitle="Secuencia de movimientos con saldos acumulados."
      />

      <section className="rounded-2xl border border-[rgb(var(--border))] bg-[rgba(var(--panel),0.7)] p-6 shadow-sm">
        {!productoId ? (
          <p className="text-sm text-muted">Seleccioná un movimiento en la lista para ver el detalle.</p>
        ) : fetching ? (
          <p className="text-sm text-muted">Cargando movimientos…</p>
        ) : error ? (
          <p className="text-sm text-red-300">{error.message}</p>
        ) : movimientos.length ? (
          <div className="space-y-6">
            {resumen ? (
              <div className="flex flex-wrap gap-4 text-sm text-[rgb(var(--fg))]">
                <span className="rounded-full bg-[rgba(var(--bg),0.65)] px-3 py-1">Stock: {formatNumber(resumen.cantidad)}</span>
                <span className="rounded-full bg-[rgba(var(--bg),0.65)] px-3 py-1">
                  Costo promedio: ${formatNumber(resumen.valorPromedio)}
                </span>
                <span className="rounded-full bg-[rgba(var(--bg),0.65)] px-3 py-1">
                  Sucursal: {sucursalId || 'General'}
                </span>
              </div>
            ) : null}

            <div className="overflow-hidden rounded-2xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.6)]">
              <table className="w-full text-left text-sm">
                <thead className="bg-[rgba(var(--subpanel),0.35)] text-xs uppercase tracking-wide text-muted">
                  <tr>
                    <th className="px-4 py-3">Fecha</th>
                    <th className="px-4 py-3">Tipo</th>
                    <th className="px-4 py-3">Referencia</th>
                    <th className="px-4 py-3 text-right">Cantidad</th>
                    <th className="px-4 py-3 text-right">Costo unitario</th>
                    <th className="px-4 py-3 text-right">Saldo cantidad</th>
                    <th className="px-4 py-3 text-right">Saldo valor</th>
                    <th className="px-4 py-3">Usuario</th>
                  </tr>
                </thead>
                <tbody>
                  {movimientos.map((item) => {
                    const isSelected = movimiento?.id === item.id;
                    return (
                      <tr
                        key={item.id}
                        className={`border-t border-[rgb(var(--border))]/60 ${
                          isSelected ? 'bg-[rgba(var(--brand),0.12)] text-[rgb(var(--brand))]' : ''
                        }`}
                      >
                        <td className="px-4 py-3">{item.fecha ? new Date(item.fecha).toLocaleString() : '—'}</td>
                        <td className="px-4 py-3">{item.tipoMov}</td>
                        <td className="px-4 py-3">{`${item.referenciaTipo} #${item.referenciaId}`}</td>
                        <td className="px-4 py-3 text-right">{formatNumber(item.cantidad)}</td>
                        <td className="px-4 py-3 text-right">{formatNumber(item.costoUnit)}</td>
                        <td className="px-4 py-3 text-right">{formatNumber(item.saldoCantidad)}</td>
                        <td className="px-4 py-3 text-right">{formatNumber(item.saldoValor)}</td>
                        <td className="px-4 py-3 text-xs text-muted">{item.usuario || '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <p className="text-sm text-muted">No encontramos movimientos para los filtros indicados.</p>
        )}
      </section>

      <StickyActions
        left={
          <button
            type="button"
            onClick={onBack}
            className="rounded-xl border border-[rgb(var(--border))] bg-transparent px-4 py-2 text-sm font-medium text-[rgb(var(--fg))] transition hover:bg-[rgba(var(--fg),0.05)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
          >
            Volver
          </button>
        }
        right={<p className="text-sm text-muted">Usá este detalle para auditar movimientos contables.</p>}
      />
    </div>
  );
}
